import { useState, type FormEvent } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { Button, Card, CopyValue, Field, Input, Textarea, UiLink } from '@algoboo/ui'
import { useApiMutation } from '../lib/api'
import { usePageMeta } from '../lib/meta'
import { PageHeader } from '../components/Shell'
import { useSession } from '../components/session'

/** `/docs/support`: a support ticket (POST /support/tickets). Flat route: `/docs` has no outlet. */
export const Route = createFileRoute('/docs_/support')({
  validateSearch: z.object({ from: z.string().max(2000).optional().catch(undefined) }),
  component: SupportPage,
})

function initialPage(from: string | undefined): string {
  if (from) return from
  if (typeof document === 'undefined') return ''
  try {
    const ref = document.referrer ? new URL(document.referrer) : null
    return ref && ref.origin === window.location.origin ? ref.href : ''
  } catch {
    return ''
  }
}

function SupportPage() {
  usePageMeta('Support', 'Ask the algo.boo team for help.')
  const { from } = Route.useSearch()
  const { me } = useSession()
  const [f, setF] = useState(() => ({ subject: '', body: '', contact: '', pageUrl: initialPage(from) }))
  const send = useApiMutation('createSupportTicket')
  const subjectOk = f.subject.trim().length >= 3
  const bodyOk = f.body.trim().length >= 10
  const [tried, setTried] = useState(false)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    setTried(true)
    if (!subjectOk || !bodyOk) return
    send.mutate({ body: { subject: f.subject.trim(), body: f.body.trim(), contact: f.contact.trim() || null, pageUrl: f.pageUrl.trim() || null } })
  }

  return (
    <>
      <PageHeader eyebrow="Docs" title="Support" lede="Something broken, unclear or missing? Tell us what happened. We answer by email or on the handle you leave." />
      {send.data ? (
        <Card className="max-w-2xl p-5" data-testid="support-done" role="status">
          <p className="text-[15px] font-semibold text-ink">Ticket received</p>
          <p className="mt-1 text-[13.5px] text-ink-2">Keep this reference if you write again:</p>
          <div className="mt-3">
            <CopyValue value={send.data.id} />
          </div>
          <p className="mt-4 text-[13px] text-ink-3">
            Meanwhile the <UiLink href="/docs" className="text-accent hover:underline">docs</UiLink> cover permissions, fees and P&amp;L.
          </p>
        </Card>
      ) : (
        <form onSubmit={submit} noValidate aria-label="Support ticket" className="rh-card max-w-2xl space-y-4 rounded-xl p-5">
          <Field htmlFor="s-subject" label="Subject" error={tried && !subjectOk ? 'At least 3 characters.' : null}>
            <Input id="s-subject" maxLength={200} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />
          </Field>
          <Field htmlFor="s-body" label="What happened" error={tried && !bodyOk ? 'At least 10 characters.' : null} hint="Steps, the agent or token involved, transaction hashes.">
            <Textarea id="s-body" rows={7} maxLength={8000} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
          </Field>
          <Field htmlFor="s-contact" label="How to reach you" optional hint={me ? 'Signed in: we can also answer through your account.' : 'An email address or a handle.'}>
            <Input id="s-contact" maxLength={200} value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} />
          </Field>
          <Field htmlFor="s-page" label="Page" optional>
            <Input id="s-page" maxLength={2000} value={f.pageUrl} onChange={(e) => setF({ ...f, pageUrl: e.target.value })} />
          </Field>
          {send.error && (
            <p role="alert" className="text-[13px] text-danger">
              {send.error.message}
            </p>
          )}
          <Button type="submit" variant="primary" loading={send.isPending} data-testid="support-submit">
            Send
          </Button>
        </form>
      )}
    </>
  )
}
